import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
  Chip,
} from '@mui/material'
import shortenString from '../../../../../utils/string'
import {
  faEye,
  faMagnifyingGlass,
  faTrash,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { Controller } from 'react-hook-form'
import { useOrgan } from '../../../../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../../../../layouts/PermanentSidebarMUI'
import { useCallback, useMemo } from 'react'
import { ModalSearchProduct } from '../../../../../components/forms/product/ModalSearchProduct'
import { useState } from 'react'
import { BaseDialog } from '../../../../../components/modals/BaseDialog'
import DetailsSectionRenderer from '../../../../../components/DetailsSectionRenderer'
import useBaseService from '../../../../../hooks/services/useBaseService'
import { entities } from '../../../../../constants/entities'
import { useNavigate } from 'react-router-dom'

import { detailsEquipmentFields } from '../../../../../constants/forms/equipmentFields'
import { detailsProductFields } from '../../../../../constants/forms/almoxarifado/productFields'

export const Biped = ({
  data = [],
  columns,
  assets,
  inputs,
  volumes,
  getTotalAlreadyAllocated,
  control,
  firstInputRef,
  inputRefs,
  isLoading,
  isDebouncePending,
  handleRemoveItem,
  debouncedAddEquipment,
  flushAddEquipment,
  debouncedAddProduct,
  handleRemoveBipedItem,

  //   * Modal de Busca do produto
  assetsData,
  setProducts,
  setValue,
  serviceOrderId,
  serviceOrderItemsData,
}) => {
  // Modal de busca de insumos
  const queryClient = useQueryClient()
  const [openProductSearchModal, setOpenProductSearchModal] = useState(false)
  const [productSearchItem, setProductSearchItem] = useState(null)
  const [productFilter, setProductFilter] = useState('')
  const [selectedScannerGroup, setSelectedScannerGroup] = useState(null)

  const navigate = useNavigate()
  const { put } = useBaseService(navigate)

  const getServiceOrderItemId = useCallback((item) => {
    if (item.is_extra) return null
    if (item.service_order_item_id) return item.service_order_item_id
    if (item.service_order_item?.id) return item.service_order_item.id
    // Linhas de equipamento do ALUGUEL com contrato não carregam o id do
    // item da requisição: resolve pelo item de contrato, como no mapeamento
    if (!Array.isArray(serviceOrderItemsData)) return null
    const contractItemId = item.contract_item?.id ?? item.contract_item_id
    if (contractItemId == null) return null
    const rootItemId = item.contract_item?.root_item_id
    const soItem = serviceOrderItemsData.find(
      (i) =>
        i.contract_item_id != null &&
        (String(i.contract_item_id) === String(contractItemId) ||
          (rootItemId != null &&
            String(i.contract_item_id) === String(rootItemId)))
    )
    return soItem?.id ?? null
  }, [serviceOrderItemsData])

  // Agrupa repetições da mesma linha da requisição e tipo. O ID da linha
  // mantém separadas linhas distintas mesmo quando descrevem o mesmo tipo.
  const scannerGroups = useMemo(() => {
    const groups = new Map()
    data.forEach((item) => {
      const serviceOrderItemId = getServiceOrderItemId(item)
      const typeId = item.type?.id ?? item.product_id ?? item.contract_item?.product_id
      const typeLabel = item.type?.name ?? item.contract_item?.product?.name ?? item.alias ?? item.name ?? 'Item da requisição'
      const groupOwner = item.is_extra
        ? item.parent_identify ?? serviceOrderItemId ?? 'kit'
        : serviceOrderItemId ?? 'line'
      const groupType = item.is_extra
        ? `extra-${item.extra_id ?? item.id}`
        : typeId ?? item._identify ?? item.identify
      const key = `${groupOwner}:${groupType}`
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label: typeLabel,
          item,
          rows: [],
          isProduct: 'product_id' in item,
        })
      }
      groups.get(key).rows.push(item)
    })
    return Array.from(groups.values())
  }, [data, getServiceOrderItemId])

  const expandedRows = scannerGroups.find((group) => group.key === selectedScannerGroup)?.rows ?? []
  const expandedRowIndexes = new Map(data.map((item, index) => [item.identify, index]))

  const getItemStockStatus = (item) => {
    // Produtos trazem o objeto completo
    if (item.service_order_item?.stock_status) {
      return item.service_order_item.stock_status
    }
    // Equipamentos: buscar no serviceOrderItemsData pelo id
    const soItemId = getServiceOrderItemId(item)
    if (soItemId && Array.isArray(serviceOrderItemsData)) {
      const soItem = serviceOrderItemsData.find((i) => i.id === soItemId)
      return soItem?.stock_status || 'in_stock'
    }
    return 'in_stock'
  }

  const handleToggleStockStatus = async (item) => {
    const soItemId = getServiceOrderItemId(item)
    if (!soItemId || !serviceOrderId) return

    const currentStatus = getItemStockStatus(item)
    const newStatus = currentStatus === 'awaiting_purchase' ? 'in_stock' : 'awaiting_purchase'

    await put(
      entities.serviceOrders.serviceOrderItemStockStatus(
        serviceOrderId,
        soItemId
      ),
      { stock_status: newStatus }
    )

    queryClient.invalidateQueries({ queryKey: ['serviceOrderItems'] })
    queryClient.invalidateQueries({ queryKey: ['contractItemsForMovement'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }

  const handleOpenProductSearchModal = (item) => {
    setProductSearchItem(item)
    queryClient.invalidateQueries({ queryKey: ['serviceOrderItems'] })
    setOpenProductSearchModal(true)
  }

  const handleCloseProductSearchModal = () => {
    setProductSearchItem(null)
    setOpenProductSearchModal(false)
  }

  const getGroupInfo = useCallback(
    (item, index) => {
      const groupKey = item._identify

      const firstOccurrenceIndex = data.findIndex(
        (eq) => eq._identify === groupKey
      )

      const count = data.filter((eq) => eq._identify === groupKey).length

      return {
        isFirstInGroup: firstOccurrenceIndex === index,
        count,
      }
    },
    [data]
  )

  const findAssetData = useCallback((assetsData, item) => {
    if (item.is_extra) {
      return assetsData[item.identify] || null
    }
    if (item._identify && assetsData[item._identify]) {
      return assetsData[item._identify]
    }
    if (item.identify && assetsData[item.identify]) {
      return assetsData[item.identify]
    }
    return null
  }, []) // Array de dependências vazio se não depender de estados mutáveis locais

  const getGroupStatus = useCallback(
    (group, filled) => {
      const total = group.rows.length
      if (total > 0 && filled === total) {
        return { label: 'Completo', color: 'success' }
      }

      const hasMissingStock = group.rows.some(
        (row) =>
          !findAssetData(row.product_id ? inputs : assets, row) &&
          getItemStockStatus(row) === 'awaiting_purchase'
      )

      if (hasMissingStock) return { label: 'Sem estoque', color: 'error' }
      if (filled > 0) return { label: 'Parcial', color: 'warning' }
      return { label: 'Não atendido', color: 'default' }
    },
    [assets, findAssetData, getItemStockStatus, inputs]
  )

  const [openDetailsModal, setOpenDetailsModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)

  const handleOpenDetailsModal = (item) => {
    if ('product' in item) {
      const code = item.code
      const findProductItem = item.service_order_item?.product?.items?.find(
        (productItem) => String(productItem.code) === String(code)
      )

      if (findProductItem) {
        const newItem = {
          ...item,
          category_name: findProductItem?.category?.name,
          condition_name: findProductItem?.condition?.name,
          product_item: findProductItem,
        }
        setSelectedItem(newItem)
        setOpenDetailsModal(true)
      }
    } else {
      setSelectedItem(item)
      setOpenDetailsModal(true)
    }
  }
  const handleCloseDetailsModal = () => {
    setOpenDetailsModal(false)
    setSelectedItem(null)
  }

  const isProductSelected =
    selectedItem !== null && 'product_id' in selectedItem

  const { selectedOrgan } = useOrgan()
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  return (
    <>
      <Box sx={{ display: 'grid', gap: 1.25, mb: 2 }}>
        {scannerGroups.map((group) => {
          const filled = group.rows.filter((row) => Boolean(findAssetData(row.product_id ? inputs : assets, row))).length
          const status = getGroupStatus(group, filled)
          return (
            <Paper
              key={group.key}
              variant="outlined"
              sx={{
                overflow: 'hidden',
                borderRadius: 2,
                bgcolor: group.isProduct ? 'grey.200' : 'background.paper',
                borderColor: group.isProduct ? 'grey.400' : 'divider',
              }}>
              <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                  <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>{group.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filled} de {group.rows.length} {group.rows.length === 1 ? 'item identificado' : 'itens identificados'}
                  </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Chip size="small" variant="outlined" label={`${filled}/${group.rows.length}`} />
                    <Chip size="small" label={status.label} color={status.color} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Button size="small" onClick={() => setSelectedScannerGroup(group.key)}>
                    Ver itens
                  </Button>
                </Box>
              </Box>
            </Paper>
          )
        })}
      </Box>
      <BaseDialog
        open={Boolean(selectedScannerGroup)}
        onClose={() => setSelectedScannerGroup(null)}
        title={scannerGroups.find((group) => group.key === selectedScannerGroup)?.label ?? 'Itens da requisição'}
        maxWidth="xl">
      {expandedRows.length > 0 && <TableContainer
        component={Paper}
        elevation={3} // Adiciona uma sombra para um visual de "cartão"
        sx={{
          borderRadius: '4px',
          overflow: 'auto',
          maxHeight: { xs: '65vh', md: '72vh' },
          boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.1)',
        }} // Bordas arredondadas
      >
        <Table
          sx={{ minWidth: 650 }}
          aria-label="tabela combinada de equipamentos e patrimônios">
          <TableHead
            sx={{
              backgroundColor: primaryColor,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}>
            <TableRow>
              {columns?.map((item, index) => (
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    color: '#FFFFFD',
                    width: item === 'Nome' ? '30%' : '8%',
                  }}
                  key={index}>
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {expandedRows.map((item) => {
              const index = expandedRowIndexes.get(item.identify)
              const isProduct = 'product_id' in item
              const assetsData = !isProduct ? assets : inputs

              // Verifica se a linha atual tem algum patrimônio preenchido
              const isFilled = !!findAssetData(assetsData, item)

              // Para verifica se é o primeiro input
              const isFirstItem = index === 0
              const { isFirstInGroup } = getGroupInfo(item, index)

              // calcula a quantidade restante para esse insumo
              let remainingForItem = 0
              if (isProduct) {
                const totalAllowed = item.requested_quantity ?? 0

                const totalAlreadyAllocated = getTotalAlreadyAllocated(
                  item ?? {},
                  findAssetData(assetsData, item) ?? {},
                  data ?? [],
                  volumes ?? [],
                  assetsData ?? {}
                )

                // eslint-disable-next-line no-unused-vars
                remainingForItem = totalAllowed - totalAlreadyAllocated
              }

              const contractItemNameShorted = isProduct
                ? shortenString(item.alias ?? '-', 28)
                : shortenString(
                    item.alias ? item.alias : (item.name ?? '-'),
                    40
                  )

              const contractItemDescription = isProduct ? '' : item.description

              return (
                <TableRow
                  key={item.identify}
                  sx={{
                    ':hover': {
                      opacity: 1,
                    },
                    transition: 'opacity 0.3s ease-in-out',
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f8f8',
                    borderTop:
                      isFirstInGroup && index !== 0
                        ? item.is_extra
                          ? 'none'
                          : '4px solid #bdbdbd'
                        : 'none',
                  }}>
                  {/* ITEM DA REQUISIÇÃO */}
                  <TableCell
                    sx={{
                      fontWeight: isProduct
                        ? 'bold'
                        : isFirstInGroup
                          ? 'bold'
                          : 'normal',
                      verticalAlign: 'middle', // Volta para middle para garantir alinhamento central
                      width: '35%',
                      padding: '8px 12px ',
                      textTransform: 'uppercase',
                      position: 'relative', // Necessário para a seta absoluta
                      // Ajustamos o paddingLeft fixo para todos, assim o texto fica alinhado
                      paddingLeft: item.is_extra ? '45px' : '12px',
                    }}>
                    {item.is_extra && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '20px', // Posiciona a seta no recuo
                          top: '0px', // Começa do topo da célula
                          width: '18px', // Comprimento da curva horizontal
                          height: '22px', // Altura da linha vertical
                          borderLeft: '2px solid #cbd5e1',
                          borderBottom: '2px solid #cbd5e1',
                          borderRadius: '0 0 0 8px', // A curvinha do YouTube
                          pointerEvents: 'none',
                        }}
                      />
                    )}

                    <Tooltip
                      title={item.alias ? item.alias : item.name}
                      placement="top">
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'inherit',
                            lineHeight: 1.2,
                            fontSize: '0.875rem',
                          }}>
                          {contractItemNameShorted}
                        </Typography>

                        {contractItemDescription && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'gray',
                              textTransform: 'uppercase',
                              marginTop: '2px',
                              display: 'block',
                            }}>
                            {contractItemDescription}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>

                  {/* QUANTIDADE DA REQUISIÇÃO */}
                  {!isProduct ? (
                    // Quantidade (equipamento)
                    <TableCell
                      sx={{
                        fontWeight: isFirstInGroup ? 'bold' : 'normal',
                        verticalAlign: 'center',
                        width: '5%',
                      }}>
                      {/* O resumo da quantidade já é exibido na tela principal. */}
                    </TableCell>
                  ) : (
                    <>
                      {/* Q. Req */}
                      <TableCell
                        sx={{
                          fontWeight: isProduct
                            ? 'bold'
                            : isFirstInGroup
                              ? 'bold'
                              : 'normal',
                          verticalAlign: 'center',
                          width: '9%',
                        }}>
                        {item.requested_quantity ?? '-'}
                      </TableCell>
                    </>
                  )}

                  {/* TOMBO/CÓDIGO */}
                  <TableCell
                    className="py-0"
                    sx={{
                      verticalAlign: 'middle',
                      width: '16%',
                    }}>
                    <Box
                      display="flex"
                      gap={1}
                      sx={{
                        my: 1,
                      }}
                      alignItems="center">
                      <Controller
                        name={`equipmentsNumber.${item.identify}`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                if (!isProduct && field.value?.trim()) {
                                  flushAddEquipment(field.value, item.identify)
                                }
                              }
                            }}
                            inputRef={(el) => {
                              if (isFirstItem) {
                                firstInputRef.current = el
                              }
                              inputRefs.current[item.identify] = el
                            }}
                            label={
                              isLoading || isDebouncePending
                                ? 'Buscando...'
                                : !isProduct
                                  ? 'Tombo'
                                  : 'Código'
                            }
                            variant="outlined"
                            size="small"
                            fullWidth
                            onChange={(e) => {
                              const value = e.target.value
                              field.onChange(e)

                              if (value.trim() === '') {
                                handleRemoveBipedItem(item.identify)
                                return
                              }

                              if (!isProduct) {
                                debouncedAddEquipment(value, item.identify)
                              } else {
                                debouncedAddProduct(
                                  value,
                                  item.identify,
                                  'product_id',
                                  item.product_id
                                )
                              }
                            }}
                          />
                        )}
                      />

                      {isProduct && (
                        <Tooltip title="Buscar insumo" placement="top">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenProductSearchModal(item)}>
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  {/* Nome */}
                  <TableCell className="py-0" sx={{ verticalAlign: 'middle' }}>
                    {(() => {
                      const asset = findAssetData(assetsData, item)

                      if (asset) {
                        const assetNameShorted = isProduct
                          ? shortenString(asset.alias, 25)
                          : shortenString(asset.alias, 50)
                        // Detectar se é item de outra organização
                        const itemOrg =
                          asset.organization?.name ?? asset.current_organization
                        const isDifferentOrg =
                          itemOrg && itemOrg !== selectedOrgan?.name

                        return (
                          // Tooltip para mostrar o alias completo do patrimônio/insumo
                          <Tooltip title={asset.alias} placement="top">
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                              }}>
                              <Typography
                                variant="body"
                                color="textPrimary"
                                sx={{
                                  textTransform: 'uppercase',
                                }}
                                fontWeight="bold">
                                {assetNameShorted ?? asset.name}
                              </Typography>

                              {/* 🆕 Badge de transferência interna (só para produtos/insumos) */}
                              {isProduct && isDifferentOrg && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#d97706',
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}>
                                  <span>⚠️</span>
                                  <span>{itemOrg} (transferência interna)</span>
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        )
                      }
                      return (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic">
                          -
                        </Typography>
                      )
                    })()}
                  </TableCell>

                  {/* Actions */}
                  <TableCell
                    className="py-0"
                    sx={{
                      verticalAlign: 'middle',
                      width: '5%',
                    }}>
                    <Box className="d-flex justify-content-end gap-2">
                      {isFilled && (
                        <>
                          <Tooltip title="Ver detalhes" placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={`Ver informações de ${
                              item.alias ??
                              item.name ??
                              `${item.number} - ${item.name}`
                            }`}
                            onClick={() =>
                              handleOpenDetailsModal(
                                findAssetData(assetsData, item)
                              )
                            }>
                            <FontAwesomeIcon icon={faEye} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar" placement="top">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveBipedItem(item.identify)}
                            color="error"
                            aria-label={`Remover ${
                              item.alias ??
                              item.name ??
                              `${item.number} - ${item.name}`
                            }`}>
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </Tooltip>
                        </>
                      )}
                      {getServiceOrderItemId(item) && (
                          <Tooltip
                              title={
                              getItemStockStatus(item) === 'awaiting_purchase'
                                ? 'Sem estoque — aguardando compra. Clique para marcar como disponível.'
                                : 'Estoque disponível. Clique para marcar como sem estoque e aguardando compra.'
                            }
                            placement="top">
                            <IconButton
                              size="small"
                              onClick={() => handleToggleStockStatus(item)}
                              color={
                                getItemStockStatus(item) === 'awaiting_purchase'
                                  ? 'warning'
                                  : 'default'
                              }
                              aria-label={
                                getItemStockStatus(item) === 'awaiting_purchase'
                                  ? 'Sem estoque, aguardando compra. Marcar como disponível'
                                  : 'Estoque disponível. Marcar como sem estoque, aguardando compra'
                              }>
                              <FontAwesomeIcon icon={faTriangleExclamation} />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>}
      </BaseDialog>
      {/* MODAL DE BUSCA DE PRODUTOS */}
      <ModalSearchProduct
        openProductSearchModal={openProductSearchModal}
        handleCloseProductSearchModal={handleCloseProductSearchModal}
        productFilter={productFilter}
        setProductFilter={setProductFilter}
        productSearchItem={productSearchItem}
        findAssetData={findAssetData}
        assetsData={inputs}
        setProducts={setProducts}
        setValue={setValue}
        debouncedAddProduct={debouncedAddProduct}
      />

      {/* Modal de detalhes do item*/}
      <BaseDialog
        open={openDetailsModal}
        onClose={handleCloseDetailsModal}
        title={
          // Caso seja equipamento
          !isProductSelected ? 'Detalhes do Patrimonio' : 'Detalhes do Insumo'
        }>
        <DetailsSectionRenderer
          formData={selectedItem}
          sections={
            // Caso seja equipamento
            !isProductSelected ? detailsEquipmentFields : detailsProductFields
          }
          readOnly
        />
      </BaseDialog>
    </>
  )
}
